<script>
import { mapActions, mapGetters } from 'vuex'
import flatten from 'lodash/flatten'
import ModalFormMixin from '@lib/mixins/modal-form'
import ClosableText from '~/components/closable-text'

export default {
  name: 'ProductRoleModal',
  components: {
    ClosableText,
  },
  mixins: [ModalFormMixin],
  props: {
    productId: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    role: {
      type: Object,
      default: () => ({}),
    },
    roles: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      progress: false,
      form: {
        name: '',
        friendlyName: '',
        assignableRoles: [],
      },
    }
  },
  computed: {
    ...mapGetters('products', ['products']),
    rules() {
      const { name } = this.form
      const { role } = this
      const allRoles = flatten(this.products.map(p => {
        return p.roles
      }))

      const isTaken = allRoles.find(item => item.name === name)

      return {
        name: [
          {
            required: true,
            message: 'Name field is required',
          },
          {
            validator: (rule, value, callback) => {
              if (isTaken && name !== role.name) {
                callback(new Error(`Name ${name} is taken, must be unique`))
              } else {
                callback()
              }
            },
          },
        ],
        friendlyName: [
          {
            required: true,
            message: 'Friendly Name field is required',
          },
        ],
      }
    },
    modalName() {
      return this.isEdit ? 'Edit Role' : 'Create Role'
    },
  },
  created() {
    if (this.isEdit) {
      const { form, role } = this

      form.name = role.name
      form.friendlyName = role.friendlyName
      form.assignableRoles = role.assignableRoles.map(item => item.id)
    }
  },
  methods: {
    ...mapActions('product', ['createRole', 'updateRole']),
    async onSubmit() {
      const { form, productId, role, isEdit } = this
      const formValid = await this.$refs.form.validate().catch(() => false)

      if (!formValid) {
        return
      }

      let response

      this.progress = true

      if (isEdit) {
        response = await this.updateRole({
          form,
          productId,
          roleId: role.id,
        })
      } else {
        response = await this.createRole({
          form,
          productId,
        })
      }

      this.$notifier({
        response,
        successMsg: `Role was successfully ${isEdit ? 'updated' : 'created'}`,
      })

      this.progress = false

      if (!response[0]) {
        this.$emit('close-modal', 'createRole')
      }
    },
  },
}
</script>

<template>
  <form-dialog
    :title="modalName"
    :check-form-changed="checkIfFormChanged"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item
        label="Product"
      >
        <el-input
          :value="productName"
          disabled
        />
      </el-form-item>
      <el-form-item
        label="Role Name"
        prop="name"
      >
        <el-input
          v-model="form.name"
          :rows="4"
          maxlength="255"
          placeholder="role:name"
          show-word-limit
        />
      </el-form-item>

      <closable-text
        :class="$style.closableText"
        icon="info"
        text="Role Name must be unique across all products."
      />

      <el-form-item
        label="Friendly Role Name"
        prop="friendlyName"
      >
        <el-input
          v-model="form.friendlyName"
          :rows="4"
          maxlength="255"
          show-word-limit
        />
      </el-form-item>

      <el-form-item
        label="Role Assignment Authority"
      >
        <el-select
          v-model="form.assignableRoles"
          placeholder="Select roles"
          multiple
        >
          <el-option
            v-for="(item, i) in roles"
            :key="`role-${i}`"
            :value="item.id"
            :label="item.friendlyName"
          />
        </el-select>
      </el-form-item>

      <div class="card-footer">
        <el-button
          :class="$style.button"
          class="el-button--wide"
          @click.stop="$emit('update:visible', false)"
        >
          Cancel
        </el-button>
        <el-button
          type="primary"
          :class="$style.button"
          :loading="progress"
          :disabled="progress"
          class="el-button--wide"
          @click.stop="onSubmit"
        >
          Save
        </el-button>
      </div>
    </el-form>
  </form-dialog>
</template>

<style lang="scss" module>
.button {
  display: block;
  width: 80%;
  margin: 2rem auto 0;
}

:global {
  .el-input__count-inner {
    line-height: 1;
  }
}

.closableText {
  width: 80%;
  margin: 1rem auto;
}
</style>
